import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { useAuth } from '../store';
import { calculateGlobalLeaderboard, getUserEventResults } from '../utils/globalLeaderboard';
import { formatCurrency } from '../utils';
import type { GlobalLeaderboardEntry, UserEventResult } from '../utils/globalLeaderboard';

/**
 * Global Leaderboard Page
 * Shows lifetime winnings across all events
 */
export function GlobalLeaderboardPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  
  const [leaderboard, setLeaderboard] = useState<GlobalLeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [userEvents, setUserEvents] = useState<UserEventResult[]>([]);

  useEffect(() => {
    // Wait for auth to finish loading
    if (!authLoading && !isAuthenticated) {
      navigate('/');
      return;
    }
    
    // Don't load leaderboard until auth is ready
    if (authLoading || !isAuthenticated) {
      return;
    }

    // Calculate global leaderboard
    const loadLeaderboard = async () => {
      const entries = await calculateGlobalLeaderboard();
      setLeaderboard(entries);
      setLoading(false);
    };
    
    loadLeaderboard();
  }, [isAuthenticated, authLoading, navigate]);

  const handleUserClick = async (userId: string) => {
    if (selectedUserId === userId) {
      // Close if already selected
      setSelectedUserId(null);
      setUserEvents([]);
    } else {
      setSelectedUserId(userId);
      const events = await getUserEventResults(userId);
      setUserEvents(events);
    }
  };

  const handleEventClick = (eventId: string) => {
    navigate(`/leaderboard/${eventId}`);
  };

  // Show loading while auth is initializing
  if (authLoading) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-secondary-text">Loading...</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-primary-bg flex items-center justify-center p-4">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-secondary-text">Loading leaderboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-bg pb-20">
      {/* Header */}
      <div className="bg-secondary-bg border-b border-border p-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-primary-text">Global Leaderboard</h1>
            <p className="text-sm text-secondary-text">Lifetime winnings across all events</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate('/games')}
            >
              My Games
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => navigate('/account')}
            >
              Account
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto p-4 space-y-4">
        {leaderboard.length === 0 ? (
          <Card padding="lg">
            <div className="text-center py-8">
              <p className="text-secondary-text">No leaderboard data available</p>
            </div>
          </Card>
        ) : (
          <>
            {/* Leaderboard Table */}
            <Card padding="md">
              <h2 className="text-lg font-semibold text-primary-text mb-4">All-Time Rankings</h2>
              <div className="space-y-2">
                {leaderboard.map((entry, index) => (
                  <div key={entry.userId}>
                    <div
                      className={`p-4 rounded-lg border-2 transition-colors cursor-pointer ${
                        selectedUserId === entry.userId
                          ? 'border-primary-blue bg-primary-blue/10'
                          : 'border-border bg-tertiary-bg hover:border-input-focus'
                      } ${index < 3 ? 'bg-success-green/5' : ''}`}
                      onClick={() => handleUserClick(entry.userId)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-8 text-center">
                            {index === 0 && <span className="text-2xl">🥇</span>}
                            {index === 1 && <span className="text-2xl">🥈</span>}
                            {index === 2 && <span className="text-2xl">🥉</span>}
                            {index >= 3 && (
                              <span className="text-lg font-bold text-secondary-text">
                                #{index + 1}
                              </span>
                            )}
                          </div>
                          <div>
                            <div className="font-semibold text-lg text-primary-text">
                              {entry.username}
                            </div>
                            <div className="text-sm text-secondary-text">
                              {entry.eventsParticipated} event{entry.eventsParticipated !== 1 ? 's' : ''} • {entry.eventsWon} win{entry.eventsWon !== 1 ? 's' : ''}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xl font-bold text-success-green">
                            {formatCurrency(entry.totalWinnings)}
                          </div>
                          <div className="text-xs text-secondary-text">Lifetime</div>
                        </div>
                      </div>
                    </div>

                    {/* User Events List */}
                    {selectedUserId === entry.userId && userEvents.length > 0 && (
                      <div className="mt-2 ml-12 space-y-2">
                        {userEvents.map((eventResult) => (
                          <div
                            key={eventResult.eventId}
                            className="p-3 bg-secondary-bg rounded-lg border border-border hover:border-primary-blue cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEventClick(eventResult.eventId);
                            }}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <div className="font-medium text-primary-text">
                                  {eventResult.eventName}
                                </div>
                                <div className="text-sm text-secondary-text">
                                  Rank #{eventResult.rank} • {eventResult.wonBets}/{eventResult.totalBets} bets won
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="font-semibold text-success-green">
                                  {formatCurrency(eventResult.winningsBalance)}
                                </div>
                                <div className="text-xs text-secondary-text">
                                  {eventResult.finishedAt
                                    ? new Date(eventResult.finishedAt).toLocaleDateString()
                                    : 'Active'}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}

